import { ReactElement, useCallback, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';

import { Button } from '@flos/react-ui';
import { EnqueueSnackbar, useSnackbar } from 'notistack';
import track, { errorTypes } from 'c9-tracking';

import { useAppContext } from 'context/AppContext';
import { type TConfig, type TEndpointAPIConfig, useConfigContext } from 'context/ConfigContext';

import { deleteGiftCard } from 'common/async/async-calls';

import { type TGiftCard } from '../Home.page';
import GiftCardTable from '../GiftCardTable/GiftCardTable';
import DeleteGiftCardModal from './DeleteGiftCardModal/DeleteGiftCardModal';

import styles from './ActiveGiftCardTable.module.css';

/* ==================================== Local Types ==================================== */
// ==================================== Functions
type TUseGiftCardActions = (
  deleteGiftCardEndpoint: TEndpointAPIConfig['deleteGiftCard'],
  appBasePath: TConfig['appBasePath'],
  userEmail: string,
  navigate: NavigateFunction,
  enqueueSnackbar: EnqueueSnackbar
) => {
  isOpenDeleteGiftCardModal: boolean;
  onClickAddGifCard: () => void;
  onOpenDeleteGiftCardModal: (props: { id: string; key: string }) => void;
  onCloseDeleteGiftCardModal: () => void;
  onDeleteGiftCard: () => void;
};

// ==================================== Components
type THeader = (props: { onClickAddGifCard: ReturnType<TUseGiftCardActions>['onClickAddGifCard'] }) => ReactElement;

type TActiveGiftCardTable = (props: { isLoading: boolean; giftCards: Array<TGiftCard> }) => ReactElement;

/* ==================================== Local Functions ==================================== */
const useGiftCardActions: TUseGiftCardActions = (
  deleteGiftCardEndpoint,
  appBasePath,
  userEmail,
  navigate,
  enqueueSnackbar
) => {
  const [selectedGiftCard, setSelectedGiftCard] = useState<{ key: string; id: string } | undefined>();
  const [isOpenDeleteGiftCardModal, setIsOpenDeleteGiftCardModal] = useState(false);

  const handleOnClickAddGifCard = useCallback(() => navigate('/add'), [navigate]);

  const handleOnOpenDeleteGiftCardModal: ReturnType<TUseGiftCardActions>['onOpenDeleteGiftCardModal'] = useCallback(
    (selectedGiftCard) => {
      setSelectedGiftCard(selectedGiftCard);
      setIsOpenDeleteGiftCardModal(true);
    },
    []
  );

  const handleOnCloseDeleteGiftCardModal = useCallback(() => {
    setIsOpenDeleteGiftCardModal(false);
  }, []);

  const handleOnDeleteGiftCard = useCallback(() => {
    const { id, key } = selectedGiftCard!;
    enqueueSnackbar(`Deleting GiftCard of type: ${key}`);

    deleteGiftCard(deleteGiftCardEndpoint, id, key)
      .then(() => {
        track.interact(userEmail + ' deleted campaign ' + key.toString() + ' ID: ' + id);
        window.location.href = appBasePath;
      })
      .catch(() => {
        enqueueSnackbar(`Error deleting gift card`, { variant: 'error' });
        track.fault('Failed to delete giftcard', errorTypes.tech, 'Delete Giftcard');
      })
      .finally(() => {
        setIsOpenDeleteGiftCardModal(false);
      });
  }, [deleteGiftCardEndpoint, appBasePath, userEmail, selectedGiftCard, enqueueSnackbar]);

  return {
    isOpenDeleteGiftCardModal,
    onClickAddGifCard: handleOnClickAddGifCard,
    onOpenDeleteGiftCardModal: handleOnOpenDeleteGiftCardModal,
    onCloseDeleteGiftCardModal: handleOnCloseDeleteGiftCardModal,
    onDeleteGiftCard: handleOnDeleteGiftCard
  };
};

/* ==================================== Local Components ==================================== */
const Header: THeader = ({ onClickAddGifCard }) => {
  return (
    <div className={styles['header']}>
      <div className={styles['header-title']}>Dansk vintertid vælges som tid GMT+1, dansk sommertid som GMT +2</div>
      <Button theme="primary" onClick={onClickAddGifCard}>
        Tilføje Gavekort
      </Button>
    </div>
  );
};

/* ==================================== Main Component ==================================== */
const ActiveGiftCardTable: TActiveGiftCardTable = ({ isLoading, giftCards }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const {
    user: {
      userDetails: { email: userEmail }
    }
  } = useAppContext();
  const {
    appBasePath,
    endpointAPI: { deleteGiftCard: deleteGiftCardEndpoint }
  } = useConfigContext();

  const {
    isOpenDeleteGiftCardModal,
    onClickAddGifCard,
    onOpenDeleteGiftCardModal,
    onCloseDeleteGiftCardModal,
    onDeleteGiftCard
  } = useGiftCardActions(deleteGiftCardEndpoint, appBasePath, userEmail, navigate, enqueueSnackbar);

  return (
    <>
      <div className={styles['root']}>
        <Header onClickAddGifCard={onClickAddGifCard} />

        <GiftCardTable
          isLoading={isLoading}
          giftCards={giftCards}
          hasAction
          onOpenDeleteGiftCardModal={onOpenDeleteGiftCardModal}
        />
      </div>

      {isOpenDeleteGiftCardModal && (
        <DeleteGiftCardModal onSubmit={onDeleteGiftCard} onDismiss={onCloseDeleteGiftCardModal} />
      )}
    </>
  );
};

export default ActiveGiftCardTable;
